import React, { useState } from 'react';
import styled from 'styled-components';
import { DocumentFolderDto } from '../../services/folderDocumentService';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  width: 100%;
  max-width: 420px;
  margin: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease;

  &:hover {
    background: #e5e7eb;
    color: #374151;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;


const FolderInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const FolderIcon = styled.span`
  font-size: 32px;
`;

const FolderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FolderName = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
`;

const FolderStats = styled.span`
  font-size: 13px;
  color: #6b7280;
`;

const WarningText = styled.p`
  margin: 0;
  padding: 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  color: #dc2626;
  line-height: 1.5;
`;

const ErrorMessage = styled.span`
  font-size: 13px;
  color: #dc2626;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-start;
  margin-top: 8px;
`;

const Button = styled.button<{ variant?: 'danger' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.variant === 'danger' ? `
    background: #dc2626;
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background: #b91c1c;
    }
  ` : `
    background: white;
    color: #374151;
    border: 2px solid #e5e7eb;

    &:hover:not(:disabled) {
      background: #f9fafb;
      border-color: #d1d5db;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// ============================================================================
// COMPONENT PROPS
// ============================================================================

interface DeleteFolderConfirmModalProps {
  folder: DocumentFolderDto;
  onClose: () => void;
  onDelete: () => Promise<void>;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const DeleteFolderConfirmModal: React.FC<DeleteFolderConfirmModalProps> = ({
  folder,
  onClose,
  onDelete,
}) => {
  const [error, setError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const hasDocuments = folder.documentCount > 0;

  const handleDelete = async () => {
    if (hasDocuments) {
      setError('לא ניתן למחוק תיקיה שמכילה מסמכים. יש למחוק את המסמכים קודם.');
      return;
    }

    setIsDeleting(true);
    setError('');
    try {
      await onDelete();
    } catch (error: any) {
      const message = error.response?.data || 'שגיאה במחיקת התיקיה';
      setError(message);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Overlay>
      <Modal>
        <Header>
          <Title>מחיקת תיקיה</Title>
          <CloseButton onClick={onClose}>✕</CloseButton>
        </Header>

        <Content>
          <FolderInfo>
            <FolderIcon>📁</FolderIcon>
            <FolderDetails>
              <FolderName>{folder.name}</FolderName>
              <FolderStats>
                {folder.documentCount} מסמכים
                {folder.newDocumentCount > 0 && ` (${folder.newDocumentCount} חדשים)`}
              </FolderStats>
            </FolderDetails>
          </FolderInfo>

          {hasDocuments && (
            <WarningText>
              לא ניתן למחוק תיקיה שמכילה מסמכים. יש למחוק את כל המסמכים מהתיקיה לפני מחיקתה.
            </WarningText>
          )}

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <ButtonGroup>
            <Button
              variant="danger"
              onClick={handleDelete}
              disabled={isDeleting || hasDocuments}
            >
              {isDeleting ? 'מוחק...' : 'מחק תיקיה'}
            </Button>
            <Button onClick={onClose} disabled={isDeleting}>
              ביטול
            </Button>
          </ButtonGroup>
        </Content>
      </Modal>
    </Overlay>
  );
};

export default DeleteFolderConfirmModal;
